/**
 * the projects slice, keyed by project TDO: the membership of the projects
 * this session has read, WHEN that membership was last decided, and the merged-document settings that hang off them
 * — names, filename options, Bates stamping (VE-30105) and watermarks
 * (VE-30104), each with the in-flight state of its optimistic write.
 */
import { TDOId } from '@common-modules/universal/models/Brands';
import type { CombinedAssetSummary } from './combineServices';
import type { ProjectBatesStamp } from './batesStamp';
import type { ProjectRedactionRecord } from './projectRedactionAsset';
import type { MergedFilenameOptions } from './models';

export const namespace = 'vtn-redact-state-projects';

/**
 * Where an optimistic watermark write has got to — see `redactionWrite`.
 *
 * `saving` carries what it overwrote, because by the time the saga runs the
 * previous record is already gone from the slice; `failed` carries nothing
 * but itself, and the next write replaces it.
 */
export type ProjectRedactionWrite =
  | {
      readonly status: 'saving';
      /**
       * WHICH write this slot belongs to. Results name the write they are
       * about, so a result that arrives for a superseded one cannot speak for
       * the write that replaced it.
       */
      readonly writeId: string;
      readonly rollback: ProjectRedactionRecord;
    }
  | { readonly status: 'failed' };

export interface ProjectsState {
  /** Members by project TDO id, as last read from the server. */
  readonly membersByProject: Readonly<Record<string, readonly TDOId[]>>;
  /**
   * When each project's membership was last DECIDED — the project record's
   * `modifiedDateTime`, which `withMembers` and `buildNewProjectDetails`
   * stamp and nothing else touches.
   *
   * It is what separates a file that ARRIVED after the decision from one the
   * reviewer deliberately left out of it. Both are non-members and no other
   * stored fact tells them apart, so an offer to add "recent" non-members
   * would nag about files somebody had already said no to.
   */
  readonly membershipDecidedAt: Readonly<Record<string, string>>;
  /** Project TDO ids with a read in flight. */
  readonly loading: Readonly<Record<string, boolean>>;
  /** Project TDO ids with a membership write in flight. */
  readonly saving: Readonly<Record<string, boolean>>;
  /**
   * REQUESTS, by case id, whose project TDO is being minted.
   */
  readonly creating: Readonly<Record<string, boolean>>;
  /**
   * Project TDO ids whose merged document is being built.
   */
  readonly combining: Readonly<Record<string, boolean>>;
  /**
   * The version the rebuild in flight was issued, once the server has settled
   * it. Absent for the moment between the press and that lookup returning.
   */
  readonly combiningVersion: Readonly<Record<string, number>>;
  /**
   * The project whose combined document is on screen in the Redacted Files
   * tab, or null. Survives a reload via the `combined` query param — see
   * SET_COMBINED_PREVIEW.
   */
  readonly previewingCombinedFor: string | null;
  /**
   * The merged document's display name per project — the project TDO's own
   * `name`, initialised from the request and renameable. Absent until the
   * project has been looked up.
   */
  readonly nameByProject: Readonly<Record<string, string>>;
  /**
   * The newest merged document per project, or null where one has been looked
   * for and does not exist. A project absent from this map has not been
   * looked up at all — the editor must not read "no entry" as "never merged".
   */
  readonly combinedByProject: Readonly<
    Record<string, CombinedAssetSummary | null>
  >;
  /**
   * Projects whose merged-document lookup FAILED, which is a third answer and
   * not either of the two above.
   */
  readonly combinedLoadFailed: Readonly<Record<string, boolean>>;
  /**
   * How each project's merged filename is assembled. Absent until the project
   * has been looked up; callers fall back to the defaults, which is what an
   * unanswered project is named under anyway.
   */
  readonly filenameOptionsByProject: Readonly<
    Record<string, MergedFilenameOptions>
  >;
  /**
   * Bates settings configured once for a merged document and applied to every
   * member (VE-30105). Absent until the project has been looked up; null once
   * it has been and carries none, which is a different answer.
   */
  readonly batesByProject: Readonly<Record<string, ProjectBatesStamp | null>>;
  /**
   * What an optimistic Bates write overwrote, kept until it is confirmed or
   * undone. WRAPPED, because `previous` has three values and the slot has to
   * keep them apart: a record, `null` for "the set carried no stamping", and
   * `undefined` for "the project had not been looked up". Restoring the last
   * as `null` tells every later reader the lookup came back empty when it
   * never came back at all. Absence of the KEY is what means no write in
   * flight — the same shape `renameRollback` uses.
   */
  /**
   * The merged document's `project-redaction-data` record per project
   * (VE-30104) — its watermarks today.
   *
   * A DISCRIMINATED UNION, not `record | null | undefined`. The Bates slice
   * above uses the triple and five readers collapsed "not looked up" into
   * "carries none", each then answering confidently while a lookup was
   * outstanding or permanently after one failed. A missing key here reads as
   * `unread` through the selector and nothing else, so a caller has to name
   * the state it is handling.
   */
  readonly redactionByProject: Readonly<Record<string, ProjectRedactionRecord>>;
  /**
   * WHERE AN OPTIMISTIC WATERMARK WRITE HAS GOT TO — one slot, three states.
   *
   * Absent is idle, `saving` carries what the write overwrote, and `failed`
   * is the verdict the next write clears. It began as two booleans over two
   * maps, which is three states expressed in four and the exact shape
   * `ProjectRedactionRecord` was introduced to stop, reintroduced one slice
   * over: "no write in flight" and "the last one failed" were both the
   * absence of a rollback key, and a panel waiting on that alone said
   * "Updated" while the failure snackbar was on screen.
   *
   * The rollback needs NO WRAPPER, unlike `batesRollback` — the record type
   * can already say "unread", so restoring that state is just storing it.
   * That is the payoff of the union; the Bates slot needs an envelope to tell
   * an absent key from a null one, and this does not.
   */
  readonly redactionWrite: Readonly<Record<string, ProjectRedactionWrite>>;
  readonly batesRollback: Readonly<
    Record<string, { readonly previous: ProjectBatesStamp | null | undefined }>
  >;
  /**
   * What a rename overwrote, kept until it is confirmed or undone.
   */
  readonly renameRollback: Readonly<
    Record<
      string,
      {
        readonly name: string | undefined;
        readonly options: MergedFilenameOptions | undefined;
        readonly assetName: string | undefined;
      }
    >
  >;
}

export const defaultState: ProjectsState = {
  membersByProject: {},
  membershipDecidedAt: {},
  loading: {},
  saving: {},
  creating: {},
  combining: {},
  combiningVersion: {},
  previewingCombinedFor: null,
  nameByProject: {},
  combinedByProject: {},
  combinedLoadFailed: {},
  filenameOptionsByProject: {},
  batesByProject: {},
  redactionByProject: {},
  redactionWrite: {},
  batesRollback: {},
  renameRollback: {},
};

export interface ProjectsSlice {
  readonly [namespace]: ProjectsState;
}
